import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import './AccessSettings.css';

const errorText = error => typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'Не удалось сохранить настройки доступа.';

export default function AccessSettings({ userId = null, onClose, onDirtyChange }) {
  const requestCounter = useRef(0);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('retail_seller');
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const applyRecord = value => { setRecord(value); setDraft({ ...value.permissions }); setDirty(false); };
  const load = useCallback(async () => {
    const requestId = ++requestCounter.current;
    setLoading(true); setError(''); setMessage('');
    try {
      const [roleResponse, userResponse] = await Promise.all([
        api.get('/api/admin/access/roles'), userId ? api.get(`/api/admin/access/users/${userId}`) : Promise.resolve(null),
      ]);
      if (requestId !== requestCounter.current) return;
      setCatalog(roleResponse.data.catalog); setRoles(roleResponse.data.roles);
      applyRecord(userResponse?.data || roleResponse.data.roles.find(role => role.role === selectedRole));
    } catch (err) { if (requestId === requestCounter.current) setError(errorText(err)); }
    finally { if (requestId === requestCounter.current) setLoading(false); }
  }, [selectedRole, userId]);
  useEffect(() => { void load(); return () => { requestCounter.current += 1; }; }, [load]);
  useEffect(() => { onDirtyChange?.(dirty); return () => onDirtyChange?.(false); }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    const beforeLink = event => {
      if (event.target.closest?.('a[href]') && !window.confirm('Есть несохранённые настройки доступа. Покинуть страницу?')) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener('beforeunload', warn); document.addEventListener('click', beforeLink, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', beforeLink, true); };
  }, [dirty]);

  const change = (key, value) => {
    setDraft(previous => {
      const next = { ...previous };
      if (value === 'inherit') delete next[key];
      else next[key] = value === true || value === 'allow';
      return next;
    });
    setDirty(true); setMessage('');
  };
  const save = async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      const path = userId ? `/api/admin/access/users/${userId}` : `/api/admin/access/roles/${selectedRole}`;
      const response = await api.put(path, { permissions: draft, revision: record.revision });
      applyRecord(response.data);
      setMessage(userId ? 'Индивидуальные настройки сохранены.' : 'Настройки роли сохранены. Они действуют для всех сотрудников этой роли, кроме личных исключений.');
    } catch (err) { setError(errorText(err)); }
    finally { setSaving(false); }
  };
  const close = () => {
    if (dirty && !window.confirm('Закрыть без сохранения настроек доступа?')) return;
    onClose?.();
  };
  const switchRole = value => {
    if (dirty && !window.confirm('Сменить роль без сохранения изменений?')) return;
    setSelectedRole(value);
  };

  return <section className="access-settings" aria-label={userId ? 'Индивидуальный доступ' : 'Настройки ролей'}>
    <div className="access-heading">
      <div><h2>{userId ? `Доступ: ${record?.name || 'сотрудник'}` : 'Роли и доступ'}</h2>
        <p>{userId ? 'Личные исключения имеют приоритет над ролью. «По роли» возвращает общее правило.' : 'Открывайте разделы и подразделы для выбранной роли.'}</p></div>
      {onClose && <button type="button" className="btn-secondary" onClick={close} disabled={saving}>Закрыть</button>}
    </div>
    {error && <div className="error-msg" role="alert">{error} <button className="btn-secondary small" type="button" onClick={() => { if (!dirty || window.confirm('Загрузить сохранённые настройки и отменить изменения?')) void load(); }}>Обновить настройки</button></div>}
    {message && <p role="status" className="access-success">{message}</p>}
    {loading ? <div className="page-loader">Загрузка настроек…</div> : record && <>
      {!userId && <label className="access-role">Роль<select value={selectedRole} onChange={event => switchRole(event.target.value)} disabled={saving}>{roles.map(role => <option value={role.role} key={role.role}>{role.label}</option>)}</select></label>}
      {userId && <p>Роль: <strong>{record.role_name}</strong>{record.status !== 'approved' && ' · Права начнут действовать после одобрения аккаунта.'}</p>}
      {record.locked && <p className="access-note">У администратора всегда полный доступ. Его права нельзя ограничить.</p>}
      <p className="access-note">Доступ к управлению заявками, техникой и материалами включает редактирование. «Просмотр пользователей» позволяет только смотреть список; назначать роли и настраивать доступ может администратор. Редактор Академии требует открытого обучения, создание заявок — доступа к своим заявкам.</p>
      <div className="access-grid">
        {catalog.map(group => <fieldset className="access-group" key={group.id} disabled={record.locked || saving}>
          <legend>{group.label}</legend>
          {!userId && <button className="access-group-toggle" type="button" onClick={() => {
            const value = !group.items.every(item => draft[item.key]);
            setDraft(previous => ({ ...previous, ...Object.fromEntries(group.items.map(item => [item.key, value])) })); setDirty(true); setMessage('');
          }}>{group.items.every(item => draft[item.key]) ? 'Закрыть весь раздел' : 'Открыть весь раздел'}</button>}
          {group.items.map(item => {
            const inherited = record.role_permissions?.[item.key] === true;
            const value = Object.hasOwn(draft, item.key) ? (draft[item.key] ? 'allow' : 'deny') : 'inherit';
            return <label className="access-row" key={item.key}>
              <span>{item.label}</span>
              {userId ? <select aria-label={item.label} value={value} onChange={event => change(item.key, event.target.value)}>
                <option value="inherit">По роли: {inherited ? 'открыт' : 'закрыт'}</option><option value="allow">Разрешить</option><option value="deny">Запретить</option>
              </select> : <input aria-label={item.label} type="checkbox" checked={draft[item.key] === true} onChange={event => change(item.key, event.target.checked)} />}
            </label>;
          })}
        </fieldset>)}
      </div>
      {!record.locked && <div className="access-actions">
        <button className="btn-primary" type="button" disabled={!dirty || saving} onClick={save}>{saving ? 'Сохранение…' : 'Сохранить доступ'}</button>
        <button className="btn-secondary" type="button" disabled={!dirty || saving} onClick={() => { setDraft({ ...record.permissions }); setDirty(false); setError(''); }}>Отменить изменения</button>
        {userId && <button className="btn-secondary" type="button" disabled={saving || !Object.keys(draft).length} onClick={() => { setDraft({}); setDirty(true); setMessage(''); }}>Все права по роли</button>}
        {dirty && <span>Есть несохранённые изменения</span>}
      </div>}
    </>}
  </section>;
}
